import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../App';
import { useStepUp } from '../auth/StepUpProvider';
import { 
  Activity, Eye, Lock, Key, 
  AlertTriangle, CheckCircle2, Info,
  Loader2, Globe, Monitor,
  RefreshCw, ShieldCheck, ChevronDown, ChevronUp
} from 'lucide-react';

const severityColors = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200',
  info: 'bg-emerald-100 text-emerald-700 border-emerald-200'
};

const severityIcons = {
  critical: <AlertTriangle className="w-4 h-4" />,
  high: <AlertTriangle className="w-4 h-4" />,
  medium: <Eye className="w-4 h-4" />,
  low: <Info className="w-4 h-4" />,
  info: <CheckCircle2 className="w-4 h-4" />
};

const SecurityDashboard = () => {
  const { requireStepUp } = useStepUp();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [knownIpsExpanded, setKnownIpsExpanded] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error('Security dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // Step-up: sensitive action requires fresh biometric/password proof.
    const ok = await requireStepUp({ reason: 'Change your account password' });
    if (!ok) return;

    setChangingPassword(true);
    try {
      const res = await axios.post(`${API}/security/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success(res.data.message);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Unable to load security dashboard. Please try again.
        </CardContent>
      </Card>
    );
  }

  const { summary, known_ips, recommendations } = dashboard;

  return (
    <div className="space-y-5" data-testid="security-dashboard">
      {/* Security Overview Stats */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#283F8B]" />
                Security Overview
              </CardTitle>
              <CardDescription>Real-time security monitoring for your account</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchDashboard} data-testid="refresh-security-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <StatCard
              label="Active Sessions"
              sublabel="Current"
              value={summary.active_sessions}
              icon={<Monitor className="w-4 h-4" />}
              color="text-blue-600 bg-blue-50"
              testId="stat-sessions"
            />
            <StatCard
              label="Password Age"
              sublabel="Days"
              value={summary.password_age_days ?? 'N/A'}
              icon={<Key className="w-4 h-4" />}
              color={summary.password_age_days > 90 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600 bg-emerald-50'}
              testId="stat-password-age"
            />
            <StatCard
              label="Login Locations"
              sublabel="Known"
              value={known_ips?.length || 0}
              icon={<Globe className="w-4 h-4" />}
              color="text-slate-600 bg-slate-50"
              testId="stat-known-locations"
            />
          </div>
        </CardContent>
      </Card>

      {/* Security Recommendations */}
      {recommendations?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#108981]" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recommendations.map((rec, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 p-3 rounded-lg border ${severityColors[rec.severity] || severityColors.info}`}
                data-testid={`recommendation-${i}`}
              >
                {severityIcons[rec.severity] || severityIcons.info}
                <p className="text-sm">{rec.message}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Change Password */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="w-4 h-4 text-[#283F8B]" />
                Change Password
              </CardTitle>
              <CardDescription className="text-xs">Changing your password will log you out of all other devices</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              data-testid="toggle-password-form"
            >
              {showPasswordForm ? 'Cancel' : 'Change'}
            </Button>
          </div>
        </CardHeader>
        {showPasswordForm && (
          <CardContent className="space-y-3 pt-0">
            <div className="space-y-1.5">
              <Label className="text-xs">Current Password</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={e => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">New Password</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={e => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="bg-[#283F8B] hover:bg-[#283F8B]/90 w-full"
              data-testid="submit-password-change"
            >
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Password
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Known Devices / IPs
          iter1078 - Collapsed by default. Multi-location travellers can
          have 20+ IPs on record which pushed the Close-Account CTA far
          below the fold. Users tap "Show all locations" to expand. */}
      {known_ips?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <button
              type="button"
              onClick={() => setKnownIpsExpanded((v) => !v)}
              className="w-full flex items-center justify-between text-left"
              data-testid="known-ips-toggle"
            >
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#283F8B]" />
                Known Login Locations ({known_ips.length})
              </CardTitle>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                {knownIpsExpanded ? (
                  <>Hide <ChevronUp className="w-3.5 h-3.5" /></>
                ) : (
                  <>Show all locations <ChevronDown className="w-3.5 h-3.5" /></>
                )}
              </span>
            </button>
          </CardHeader>
          {knownIpsExpanded && (
            <CardContent>
              <div className="space-y-2">
                {known_ips.map((ip, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg text-sm" data-testid={`known-ip-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Globe className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{ip.location || 'Unknown location'}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{ip.ip_address}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Last: {formatTimestamp(ip.last_seen)}</p>
                      <p className="text-xs font-medium">{ip.login_count} login{ip.login_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

const StatCard = ({ label, sublabel, value, icon, color, testId }) => (
  <div className={`p-3 rounded-xl border ${color.split(' ').slice(1).join(' ')}`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <span className={color.split(' ')[0]}>{icon}</span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color.split(' ')[0]}`}>{value}</p>
    <p className="text-[10px] text-muted-foreground">{sublabel}</p>
  </div>
);

export default SecurityDashboard;
